import { useFirm } from "@app/pages/settings/useFirm";
import { CarPdfViewer } from "@app/components/PdfViewer";
import { useSimulationSimulationJobLatestSimulationRetrieveQuery } from "@app/api/carApi.generated";
import { useSimulationGroups } from "@app/pages/assetAllocation/useSimulationGroups";
import { CarPdfDocument } from "@app/components/PdfDocument";
import { useUser } from "@app/pages/settings/useUser";
import { CarPdfPage } from "@app/components/PdfPage";
import { CarPdfBox } from "@app/components/PdfBox";
import { CarPdfTypography } from "@app/components/PdfTypography";
import { SimulatedForwardProbabilitiesPdfChart } from "@app/pages/assetClassAnalysis/pdfSimulatedForwardProbabilitiesReportPage/SimulatedForwardProbabilitiesPdfChart";
import { SimulatedForwardProbabilitiesPdfChartLegend } from "@app/pages/assetClassAnalysis/pdfSimulatedForwardProbabilitiesReportPage/SimulatedForwardProbabilitiesPdfChartLegend";
import { usePortfolioGroupVersionStatData } from "@app/pages/assetAllocation/usePortfolioGroupVersionStatData";
import { usePortfolioGroupInfo } from "@app/pages/assetAllocation/usePortfolioGroupInfo";
import { useChartSimulatedForwardProbabilitiesData } from "@app/pages/assetClassAnalysis/useChartSimulatedForwardProbabilitiesData";

interface ModuleSimulatedForwardProbabilitiesReportProps {
  portfolioGroupId: string;
  moduleId: string;
  data1Id: string;
  data2Id: string;
  isReal: boolean;
  isCompare: boolean;
  chartDateRange1: number;
  chartDateRange2: number;
  footer: string;
}

export const ModuleSimulatedForwardProbabilitiesReport = ({
  portfolioGroupId,
  moduleId,
  data1Id,
  data2Id,
  isReal,
  isCompare,
  chartDateRange1,
  chartDateRange2,
  footer,
}: ModuleSimulatedForwardProbabilitiesReportProps) => {
  const firm = useFirm();

  const latestSimulation =
    useSimulationSimulationJobLatestSimulationRetrieveQuery();

  const simulationGroups = useSimulationGroups();

  const groupInfo = usePortfolioGroupInfo({ portfolioGroupId });

  const { portfolioGroupVersionId, publishedVersionId, isSinglePortfolioMode } =
    groupInfo;

  const statData = usePortfolioGroupVersionStatData({
    versionId: portfolioGroupVersionId,
    publishedVersionId,
    filterByModuleId: moduleId,
  });

  const isLoading =
    firm.isLoading ||
    groupInfo.isLoading ||
    statData.isLoading ||
    simulationGroups.isLoading ||
    latestSimulation.isLoading;

  const chartData = useChartSimulatedForwardProbabilitiesData({
    nominalHeatmap: statData.nominalHeatmap,
    realHeatmap: statData.realHeatmap,
    isCompare,
    isReal,
    data1Id,
    data2Id,
    chartDateRange1,
    chartDateRange2,
  });

  const user = useUser();

  const title = isCompare
    ? `Simulated Forward Probabilities ${chartData.label1} vs. ${chartData.label2}`
    : `Simulated Forward Probabilities ${chartData.label1}`;

  const moduleName = statData.getModuleNameById(moduleId);

  const reportTitle = `Simulated Forward Probabilities - ${isSinglePortfolioMode ? groupInfo.title : moduleName}`;

  return (
    <CarPdfViewer isLoading={user.isLoading || isLoading}>
      <CarPdfDocument title={title} author={user.userFullName}>
        <CarPdfPage
          reportTitle={reportTitle}
          sectionTitle="Forward Looking Analysis"
          footer={footer}
        >
          <CarPdfBox
            style={{
              marginTop: 36,
              flexDirection: "row",
              width: "100%",
              gap: 6,
            }}
          >
            <CarPdfTypography
              variant="v14Semibold"
              dense
              style={{ maxWidth: 306, lineHeight: 18 }}
            >
              {`Time Frame ${chartDateRange1} Years (${isReal ? "Real" : "Nominal"})`}
            </CarPdfTypography>
            <SimulatedForwardProbabilitiesPdfChartLegend
              style={{ marginTop: 2, marginLeft: "auto" }}
            />
          </CarPdfBox>
          <SimulatedForwardProbabilitiesPdfChart
            style={{ marginTop: 26 }}
            data={chartData}
          />
        </CarPdfPage>
      </CarPdfDocument>
    </CarPdfViewer>
  );
};
